import { findRuntimeCatalogCourse, getRuntimeCourseCatalog } from '../services/runtimeCourseCatalog.js'
import { LEGACY_1_3_CHANGED_COURSES_BY_ID, LEGACY_1_3_COURSE_IDS } from '../data/legacyCourseBaseline1_3.js'
import { COURSE_DATA } from '../data/courseData.js'
import { getCourseScheduleMode, normalizeCourseIsOnline } from './courseScheduleMode.js'

function text(value) {
  return value == null ? '' : String(value).trim()
}

function stableId(value) {
  const id = text(value)
  return id.replace(/^\d{4}-[12]-/, '')
}

function numberOrNull(value) {
  if (value == null || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const OFFICIAL_FIELDS = ['name', 'professor', 'credits', 'schedule', 'classroom', 'delivery']

function periodsFor(meeting = {}) {
  if (Array.isArray(meeting.periods)) return [...new Set(meeting.periods.map(numberOrNull).filter(Number.isInteger))].sort((a, b) => a - b)
  const start = numberOrNull(meeting.startPeriod)
  const end = numberOrNull(meeting.endPeriod ?? start)
  return start == null ? [] : Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index)
}

function daysFor(meeting = {}) {
  const days = Array.isArray(meeting.days) ? meeting.days : (meeting.day ? [meeting.day] : [])
  return days.map(text).filter(Boolean)
}

export function normalizedMeetings(course = {}) {
  const source = Array.isArray(course.meetings) && course.meetings.length ? course.meetings : []
  if (source.length) return source.map(meeting => {
    const days = daysFor(meeting)
    const periods = periodsFor(meeting)
    const startPeriod = periods[0] ?? numberOrNull(meeting.startPeriod)
    const endPeriod = periods.at(-1) ?? numberOrNull(meeting.endPeriod ?? startPeriod)
    const classroom = text(meeting.classroom || meeting.room)
    return { ...meeting, day: days[0] ?? '', days, periods, startPeriod, endPeriod, classroom, room: classroom }
  })

  const days = (Array.isArray(course.days) ? course.days : (course.day ? [course.day] : [])).map(text).filter(Boolean)
  const periods = periodsFor(course)
  const startPeriod = periods[0] ?? numberOrNull(course.startPeriod)
  const endPeriod = periods.at(-1) ?? numberOrNull(course.endPeriod ?? startPeriod)
  const classroom = text(course.classroom || course.room)
  return days.length || startPeriod != null || classroom
    ? [{ day: days[0] ?? '', days, periods, startPeriod, endPeriod, classroom, room: classroom }]
    : []
}

export function scheduleProjection(course = {}) {
  const meetings = normalizedMeetings(course)
  const first = meetings[0] ?? {}
  const days = [...new Set(meetings.flatMap(daysFor))]
  return {
    day: days[0] ?? '',
    days,
    startPeriod: first.startPeriod ?? null,
    endPeriod: first.endPeriod ?? first.startPeriod ?? null,
    periods: first.periods ?? [],
    meetings,
  }
}

function scheduleSignature(course = {}) {
  const { meetings } = scheduleProjection(course)
  return JSON.stringify(meetings.map(({ day, days, periods, startPeriod, endPeriod }) => ({ day, days, periods, startPeriod, endPeriod })))
}

function catalogRoom(course) {
  if (!course) return ''
  if (text(course.room)) return text(course.room)
  if (text(course.classroom)) return text(course.classroom)
  return (Array.isArray(course.meetings) ? course.meetings : [])
    .map(meeting => text(meeting?.classroom || meeting?.room))
    .find(Boolean) || ''
}

export function officialProjection(course = {}) {
  const rawSchedule = scheduleProjection(course)
  const isOnline = normalizeCourseIsOnline(course)
  const schedule = rawSchedule
  const classroom = catalogRoom(course)
  return {
    name: text(course.name || course.courseName),
    professor: text(course.professor),
    credits: numberOrNull(course.credits ?? course.credit) ?? 0,
    isOnline,
    classroom,
    category: text(course.category || course.type),
    department: text(course.department),
    division: text(course.division || course.section),
    subjectKey: text(course.subjectKey || course.courseCode),
    ...schedule,
  }
}

export function fieldOverrides(course = {}) {
  const explicit = course.officialFieldOverrides && typeof course.officialFieldOverrides === 'object'
    ? course.officialFieldOverrides
    : {}
  const fields = new Set(OFFICIAL_FIELDS.filter(field => explicit[field] === true))
  if (course.isClassroomOverridden || course.classroomOverride) fields.add('classroom')
  return fields
}

function legacyBaseline(course, official) {
  const id = stableId(course?.id || course?.courseId || course?.sectionId)
  if (!LEGACY_1_3_COURSE_IDS.has(id)) return null
  return LEGACY_1_3_CHANGED_COURSES_BY_ID[id] ?? null
}

function classroomValues(course = {}) {
  return new Set([
    text(course.classroom), text(course.room),
    ...normalizedMeetings(course).map(meeting => text(meeting.classroom || meeting.room)),
  ].filter(Boolean))
}

function legacyFieldWasCustomized(course, baseline, field, official) {
  if (!baseline) return false
  const bundled = findRuntimeCatalogCourse(course, COURSE_DATA)
  const bundledOfficial = bundled ? officialProjection(bundled) : null

  if (field === 'name') {
    const val = text(course.name || course.courseName)
    return val !== baseline.name && val !== official.name && (!bundledOfficial || val !== bundledOfficial.name)
  }
  if (field === 'professor') {
    const val = text(course.professor)
    return val !== baseline.professor && val !== official.professor && (!bundledOfficial || val !== bundledOfficial.professor)
  }
  if (field === 'credits') {
    const val = numberOrNull(course.credits ?? course.credit) ?? 0
    return val !== baseline.credits && val !== official.credits && (!bundledOfficial || val !== bundledOfficial.credits)
  }
  if (field === 'schedule') {
    const sig = scheduleSignature(course)
    return sig !== scheduleSignature(baseline) && sig !== scheduleSignature(official) && (!bundledOfficial || sig !== scheduleSignature(bundledOfficial))
  }
  if (field === 'classroom') {
    const storedValues = classroomValues(course)
    const baselineValues = classroomValues(baseline)
    const officialValues = classroomValues(official)
    const bundledValues = bundledOfficial ? classroomValues(bundledOfficial) : new Set()
    return [...storedValues].some(value => !baselineValues.has(value) && !officialValues.has(value) && !bundledValues.has(value))
  }
  return false
}

function applyOfficialClassrooms(meetings, officialMeetings) {
  if (!meetings.length) return meetings
  if (officialMeetings.length === 1) {
    const classroom = officialMeetings[0].classroom
    return meetings.map(meeting => ({ ...meeting, classroom, room: classroom }))
  }
  return meetings.map((meeting, index) => {
    const matching = officialMeetings.find(candidate => scheduleSignature({ meetings: [candidate] }) === scheduleSignature({ meetings: [meeting] }))
    const classroom = matching?.classroom ?? officialMeetings[index]?.classroom ?? meeting.classroom
    return { ...meeting, classroom, room: classroom }
  })
}

function applyCustomClassrooms(meetings, storedMeetings, storedClassroom) {
  if (!meetings.length) return meetings
  if (storedMeetings.length === 1) {
    const classroom = storedMeetings[0].classroom || storedClassroom
    return meetings.map(meeting => ({ ...meeting, classroom, room: classroom }))
  }
  return meetings.map(meeting => {
    const matching = storedMeetings.find(candidate => scheduleSignature({ meetings: [candidate] }) === scheduleSignature({ meetings: [meeting] }))
    const classroom = matching?.classroom || meeting.classroom
    return { ...meeting, classroom, room: classroom }
  })
}

/**
 * Single SSOT Projection Resolver:
 * Projects a stored timetable course entry onto the active runtime catalog.
 * Formula:
 * EffectiveCourse = (isCustom ? course : (Catalog[id] ?? course)) + UserOverrides + UserOwnedProperties
 */
export function resolveTimetableProjection(course = {}, catalog = null) {
  if (!course || typeof course !== 'object') return course
  const activeCatalog = catalog ?? getRuntimeCourseCatalog()
  const matched = findRuntimeCatalogCourse(course, activeCatalog)

  // Custom / Manual course or Orphan course without catalog match
  if (!matched || course.isCustom === true) {
    const isOnline = normalizeCourseIsOnline(course)
    const onlinePatch = isOnline || course.isOnline === false ? { isOnline } : {}
    const fallbackMeetingRoom = (Array.isArray(course.meetings) ? course.meetings : [])
      .map(meeting => text(meeting?.classroom || meeting?.room))
      .find(Boolean) || ''
    const classroom = text(course.classroom) || text(course.room) || fallbackMeetingRoom
    const hasRoomAlias = Object.prototype.hasOwnProperty.call(course, 'room')
    const hasClassroomAlias = Object.prototype.hasOwnProperty.call(course, 'classroom')
    if (!classroom && !hasRoomAlias && !hasClassroomAlias && !Array.isArray(course.meetings)) {
      return { ...course, ...onlinePatch }
    }
    return {
      ...course,
      ...onlinePatch,
      classroom,
      room: classroom,
      ...(Array.isArray(course.meetings) ? {
        meetings: course.meetings.map(meeting => ({
          ...meeting,
          classroom: text(meeting?.classroom || meeting?.room) || classroom,
          room: text(meeting?.room || meeting?.classroom) || classroom,
        })),
      } : {}),
    }
  }

  const official = officialProjection(matched)
  const stored = officialProjection(course)
  const baseline = legacyBaseline(course, official)
  const overrides = fieldOverrides(course)

  // Legacy Build-13 unflagged migration bridge
  const hasFieldLevelOverrideModel = course.officialFieldOverrides && typeof course.officialFieldOverrides === 'object'
  for (const field of OFFICIAL_FIELDS) {
    if (!hasFieldLevelOverrideModel && !overrides.has(field) && legacyFieldWasCustomized(course, baseline, field, official)) {
      overrides.add(field)
    }
  }

  const schedule = overrides.has('schedule') ? stored : official
  const storedMeetings = normalizedMeetings(course)
  const officialMeetings = normalizedMeetings(matched)
  let meetings = schedule.meetings.map(meeting => ({ ...meeting }))
  
  if (overrides.has('classroom')) {
    meetings = applyCustomClassrooms(meetings, storedMeetings, stored.classroom)
  } else {
    meetings = applyOfficialClassrooms(meetings, officialMeetings)
  }

  const first = meetings[0] ?? {}
  const days = [...new Set(meetings.flatMap(daysFor))]
  const classroom = overrides.has('classroom')
    ? (stored.classroom || first.classroom || official.classroom)
    : (first.classroom || official.classroom)
  const nextOverrides = Object.fromEntries([...overrides].map(field => [field, true]))

  return {
    ...course,
    id: text(course.id || matched.id),
    name: overrides.has('name') ? text(course.name || course.courseName) : official.name,
    courseName: overrides.has('name') ? text(course.courseName || course.name) : official.name,
    professor: overrides.has('professor') ? text(course.professor) : official.professor,
    credits: overrides.has('credits') ? (numberOrNull(course.credits ?? course.credit) ?? 0) : official.credits,
    credit: overrides.has('credits') ? (numberOrNull(course.credit ?? course.credits) ?? 0) : official.credits,
    // 사용자가 일반 수업 ↔ 이러닝을 직접 바꾼 경우(delivery)에는 저장된 값을 따른다.
    isOnline: overrides.has('delivery') ? stored.isOnline : official.isOnline,
    scheduleMode: getCourseScheduleMode({ isOnline: overrides.has('delivery') ? stored.isOnline : official.isOnline }),
    category: official.category || course.category || '',
    department: official.department || course.department || '',
    division: official.division || course.division || '',
    subjectKey: official.subjectKey || course.subjectKey || '',
    day: days[0] ?? '',
    days,
    startPeriod: first.startPeriod ?? null,
    endPeriod: first.endPeriod ?? first.startPeriod ?? null,
    periods: first.periods ?? [],
    classroom,
    room: classroom,
    meetings,
    ...(Object.keys(nextOverrides).length ? { officialFieldOverrides: nextOverrides } : {}),
    ...(overrides.has('classroom') ? { isClassroomOverridden: true } : {}),
  }
}

export function resolveTimetableListProjection(courses = [], catalog = null) {
  if (!Array.isArray(courses)) return []
  return courses.filter(Boolean).map(course => resolveTimetableProjection(course, catalog))
}

export function resolveTimetableContainerProjection(value = {}, catalog = null) {
  if (!value || typeof value !== 'object') return value
  const next = { ...value }
  if (Array.isArray(value.courses)) {
    next.courses = resolveTimetableListProjection(value.courses, catalog)
  }
  if (Array.isArray(value.selectedCourses)) {
    next.selectedCourses = resolveTimetableListProjection(value.selectedCourses, catalog)
  }
  return next
}
